/**
 * RoleBindingDialog — modal create-form for new role bindings (Issue #243).
 *
 * Edit-form remount discipline:
 *   - Callers MUST supply a `key` prop on this component so React unmounts and
 *     remounts a fresh dialog instance whenever the user switches between
 *     binding contexts. This prevents stale state from a previous binding
 *     leaking into the new form.
 *
 * 红线：
 *   - 所有写入字段均明确来自表单值；前端不补默认 reason / approvalNo /
 *     secondFactorResult。
 *   - 高危角色（super_admin / boss / tech_lead）必须强制要求 approvalNo +
 *     secondFactorResult='passed'；不达到则禁用提交按钮。
 *   - 取消或关闭对话框不会改动后端状态；提交才会调用真实 API。
 */
import { useId, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type {
  CreateRoleBindingRequestDto,
  PresetRoleDto,
} from '@/hooks/useSettingsRbac';
import { isHighRiskPresetRole } from '@/lib/rbacPermissions';

export type RoleBindingDialogPresetRole = PresetRoleDto;

export interface RoleBindingDialogProps {
  readonly presetRoles: readonly RoleBindingDialogPresetRole[];
  readonly onSubmit: (input: CreateRoleBindingRequestDto) => Promise<void>;
  readonly onClose: () => void;
}

type RoleKey = CreateRoleBindingRequestDto['roleKey'];
type DataScope = CreateRoleBindingRequestDto['dataScope'];

const SECOND_FACTOR_PASSED = 'passed' as const satisfies NonNullable<
  CreateRoleBindingRequestDto['secondFactorResult']
>;

export default function RoleBindingDialog({
  presetRoles,
  onSubmit,
  onClose,
}: RoleBindingDialogProps): ReactElement {
  const formId = useId();
  const selectableRoles = useMemo(
    () => presetRoles.filter((r) => r.key !== 'channel_partner'),
    [presetRoles],
  );
  const initialRole = selectableRoles[0];

  const [roleKey, setRoleKey] = useState<RoleKey | null>(initialRole?.key ?? null);
  const [dataScope, setDataScope] = useState<DataScope | null>(
    initialRole?.defaultDataScope ?? null,
  );
  const [reason, setReason] = useState('');
  const [approvalNo, setApprovalNo] = useState('');
  const [secondFactorPassed, setSecondFactorPassed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const activeRole = useMemo<RoleBindingDialogPresetRole | null>(
    () => selectableRoles.find((r) => r.key === roleKey) ?? null,
    [selectableRoles, roleKey],
  );
  const highRisk = activeRole ? isHighRiskPresetRole(activeRole.key) : false;
  const reasonTrim = reason.trim();
  const approvalNoTrim = approvalNo.trim();
  const canSubmit =
    !submitting &&
    activeRole !== null &&
    dataScope !== null &&
    reasonTrim.length >= 4 &&
    (!highRisk || (approvalNoTrim.length > 0 && secondFactorPassed));

  function handleRoleChange(next: RoleKey): void {
    setRoleKey(next);
    const role = selectableRoles.find((r) => r.key === next);
    if (role) {
      setDataScope(role.defaultDataScope);
    }
    if (!role || !isHighRiskPresetRole(role.key)) {
      setSecondFactorPassed(false);
      setApprovalNo('');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!activeRole || !dataScope) {
      setError('请选择预置角色与数据范围。');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload: CreateRoleBindingRequestDto = {
        roleKey: activeRole.key,
        dataScope,
        reason: reasonTrim,
        ...(highRisk ? { approvalNo: approvalNoTrim, secondFactorResult: SECOND_FACTOR_PASSED } : {}),
      };
      await onSubmit(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : '创建角色绑定失败');
    } finally {
      setSubmitting(false);
    }
  }

  if (!initialRole) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        role="dialog"
        aria-modal="true"
        data-testid="role-binding-dialog"
      >
        <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg">
          <p className="text-sm text-muted-foreground">
            预置角色目录暂未加载，无法发起角色绑定。请稍后重试或刷新页面。
          </p>
          <div className="mt-4 flex justify-end">
            <Button variant="outline" onClick={onClose} data-testid="role-binding-dialog-close">
              关闭
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      data-testid="role-binding-dialog"
    >
      <div className="w-full max-w-lg rounded-lg bg-background p-6 shadow-lg">
        <div className="mb-3">
          <h4 className="text-base font-semibold">新增角色绑定</h4>
          <p className="text-xs text-muted-foreground">
            写入接口仅允许超级管理员调用。提交将记录不可变审计：操作人、被变更人、变更前后快照、原因、审批单号、IP、设备、二次验证结果。
          </p>
        </div>
        <form
          id={formId}
          className="space-y-3"
          onSubmit={handleSubmit}
          data-testid="role-binding-form"
        >
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${formId}-role`}>预置角色</Label>
            <select
              id={`${formId}-role`}
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
              value={roleKey ?? ''}
              onChange={(e) => handleRoleChange(e.target.value as RoleKey)}
              data-testid="role-binding-role-select"
              required
            >
              {selectableRoles.map((role) => (
                <option key={role.key} value={role.key}>
                  {role.displayName}（{role.key} · {role.riskLevel}）
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground" data-testid="role-binding-role-hint">
              {activeRole?.description ?? ''}
            </p>
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor={`${formId}-scope`}>数据范围</Label>
            <select
              id={`${formId}-scope`}
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
              value={dataScope ?? ''}
              onChange={(e) => setDataScope(e.target.value as DataScope)}
              data-testid="role-binding-scope-select"
              required
            >
              {(activeRole?.allowedDataScopes ?? []).map((scope) => (
                <option key={scope} value={scope}>
                  {scope}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor={`${formId}-reason`}>变更原因（≥4 字符）</Label>
            <Input
              id={`${formId}-reason`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              minLength={4}
              maxLength={500}
              placeholder="例如：完成岗位变更交接后授予 finance 角色"
              data-testid="role-binding-reason-input"
            />
          </div>

          {highRisk ? (
            <div
              className="space-y-3 rounded-md border border-destructive/40 bg-destructive/5 p-3"
              data-testid="role-binding-high-risk-block"
            >
              <p className="text-xs font-medium text-destructive">
                高危预置角色：必须提供审批单号，并完成短信二次验证后才能提交。
              </p>
              <div className="flex flex-col gap-1">
                <Label htmlFor={`${formId}-approval`}>审批单号</Label>
                <Input
                  id={`${formId}-approval`}
                  value={approvalNo}
                  onChange={(e) => setApprovalNo(e.target.value)}
                  placeholder="审批工单编号"
                  required
                  data-testid="role-binding-approval-input"
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={secondFactorPassed}
                  onChange={(e) => setSecondFactorPassed(e.target.checked)}
                  data-testid="role-binding-second-factor-checkbox"
                />
                <span>
                  我已完成短信二次验证（提交时将以 <code>secondFactorResult=passed</code> 上送后端）
                </span>
              </label>
            </div>
          ) : null}

          {error ? (
            <div className="text-sm text-destructive" data-testid="role-binding-error">
              {error}
            </div>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="role-binding-dialog-cancel"
            >
              取消
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              data-testid="role-binding-dialog-submit"
            >
              {submitting ? '提交中…' : '提交绑定申请'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
